/** IndexedDB persistence for offline receipt upload queue (blobs + metadata). */

export const RECEIPT_UPLOAD_DB = 'mrt-receipt-uploads';
export const RECEIPT_UPLOAD_DB_VERSION = 1;
export const PENDING_STORE = 'pending';
export const BLOBS_STORE = 'blobs';

export type PendingReceiptRecord = {
    localId: string;
    userId: number;
    createdAt: number;
    nextAttemptAt: number;
    lastError: string | null;
    original_filename: string;
    allocation_type: 'project_cost' | 'company_expense';
    project_id: number | null;
    project_no?: number | null;
    transaction_id: number | null;
    category: string | null;
    subcategory: string | null;
    merchant: string | null;
    amount: string | null;
    transaction_date: string | null;
    responsible_user_id: number | null;
    description: string | null;
    notes: string | null;
    fileMeta: Array<{ name: string; type: string }>;
};

export type PendingBlobRecord = {
    id: string;
    localId: string;
    index: number;
    blob: Blob;
    name: string;
    type: string;
};

export const blobRecordId = (localId: string, index: number) =>
    `${localId}:${index}`;

function openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(RECEIPT_UPLOAD_DB, RECEIPT_UPLOAD_DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(PENDING_STORE)) {
                const pending = db.createObjectStore(PENDING_STORE, {
                    keyPath: 'localId',
                });
                pending.createIndex('userId', 'userId', { unique: false });
                pending.createIndex('nextAttemptAt', 'nextAttemptAt', {
                    unique: false,
                });
            }
            if (!db.objectStoreNames.contains(BLOBS_STORE)) {
                const blobs = db.createObjectStore(BLOBS_STORE, {
                    keyPath: 'id',
                });
                blobs.createIndex('localId', 'localId', { unique: false });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    });
}

function idbReq<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () =>
            reject(request.error ?? new Error('IndexedDB request failed'));
    });
}

function idbTxDone(tx: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () =>
            reject(tx.error ?? new Error('IndexedDB transaction failed'));
        tx.onabort = () =>
            reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });
}

export async function putPendingWithBlobs(
    record: PendingReceiptRecord,
    files: Array<{ blob: Blob; name: string; type: string }>
): Promise<void> {
    const db = await openDb();
    try {
        const tx = db.transaction([PENDING_STORE, BLOBS_STORE], 'readwrite');
        const pending = tx.objectStore(PENDING_STORE);
        const blobs = tx.objectStore(BLOBS_STORE);
        pending.put(record);
        files.forEach((file, index) => {
            const row: PendingBlobRecord = {
                id: blobRecordId(record.localId, index),
                localId: record.localId,
                index,
                blob: file.blob,
                name: file.name,
                type: file.type,
            };
            blobs.put(row);
        });
        await idbTxDone(tx);
    } finally {
        db.close();
    }
}

export async function updatePendingMeta(
    localId: string,
    patch: Partial<
        Pick<PendingReceiptRecord, 'nextAttemptAt' | 'lastError'>
    >
): Promise<void> {
    const db = await openDb();
    try {
        const tx = db.transaction(PENDING_STORE, 'readwrite');
        const store = tx.objectStore(PENDING_STORE);
        const existing = await idbReq(
            store.get(localId) as IDBRequest<PendingReceiptRecord | undefined>
        );
        if (!existing) {
            await idbTxDone(tx);
            return;
        }
        store.put({ ...existing, ...patch });
        await idbTxDone(tx);
    } finally {
        db.close();
    }
}

export async function deletePending(localId: string): Promise<void> {
    const db = await openDb();
    try {
        const tx = db.transaction([PENDING_STORE, BLOBS_STORE], 'readwrite');
        const pending = tx.objectStore(PENDING_STORE);
        const blobs = tx.objectStore(BLOBS_STORE);
        pending.delete(localId);
        const index = blobs.index('localId');
        const rows = await idbReq(
            index.getAll(localId) as IDBRequest<PendingBlobRecord[]>
        );
        for (const row of rows) {
            blobs.delete(row.id);
        }
        await idbTxDone(tx);
    } finally {
        db.close();
    }
}

export async function listPendingForUser(
    userId: number
): Promise<PendingReceiptRecord[]> {
    const db = await openDb();
    try {
        const tx = db.transaction(PENDING_STORE, 'readonly');
        const index = tx.objectStore(PENDING_STORE).index('userId');
        const rows = await idbReq(
            index.getAll(userId) as IDBRequest<PendingReceiptRecord[]>
        );
        await idbTxDone(tx);
        return rows.sort((a, b) => b.createdAt - a.createdAt);
    } finally {
        db.close();
    }
}

export async function getPendingBlobs(
    localId: string
): Promise<PendingBlobRecord[]> {
    const db = await openDb();
    try {
        const tx = db.transaction(BLOBS_STORE, 'readonly');
        const index = tx.objectStore(BLOBS_STORE).index('localId');
        const rows = await idbReq(
            index.getAll(localId) as IDBRequest<PendingBlobRecord[]>
        );
        await idbTxDone(tx);
        return rows.sort((a, b) => a.index - b.index);
    } finally {
        db.close();
    }
}
